import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, MapPin, Globe, Save, Loader, CheckCircle, AlertCircle, Lock, Sparkles, Shield } from 'lucide-react';
import { API_BASE } from '../config';

export default function Profile({ t, user, setUser, setLang }) {
  const [formData, setFormData] = useState({
    name: '',
    firstname: '',
    email: '',
    phone: '',
    address: '',
    preferred_language: 'fr'
  });
  
  const [passData, setPassData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [loading, setLoading] = useState(false);
  const [passLoading, setPassLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [passMessage, setPassMessage] = useState(null);

  useEffect(() => {
    if (user?.id) {
      setLoading(true);
      fetch(`${API_BASE}/customers/${user.id}`)
        .then(res => res.json())
        .then(data => {
          setFormData({
            name: data.name || '',
            firstname: data.firstname || '',
            email: data.email || '',
            phone: data.phone || '',
            address: data.address || '',
            preferred_language: data.preferred_language || 'fr'
          });
        })
        .catch(err => console.error("Error fetching profile", err))
        .finally(() => setLoading(false));
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setMessage(null);
  };

  const handlePassChange = (e) => {
    setPassData({ ...passData, [e.target.name]: e.target.value });
    setPassMessage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_BASE}/customers/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Error');

      setMessage({ type: 'success', text: t.profile.success });
      setUser({ ...user, ...formData });
      
      if (formData.preferred_language && setLang) {
        setLang(formData.preferred_language);
      }
    } catch (err) {
      setMessage({ type: 'error', text: t.profile.error });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPassLoading(true);
    setPassMessage(null);

    if (passData.newPassword !== passData.confirmPassword) {
        setPassMessage({ type: 'error', text: t.admin?.account?.errorMatch || "Error" });
        setPassLoading(false);
        return;
    }

    try {
        const response = await fetch(`${API_BASE}/auth/update-password`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                userId: user.id,
                oldPassword: passData.oldPassword,
                newPassword: passData.newPassword
            })
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Error');

        setPassMessage({ type: 'success', text: t.profile?.passSuccess || "Success" });
        setPassData({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
        setPassMessage({ type: 'error', text: err.message });
    } finally {
        setPassLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-white">
      {/* En-tête élégant */}
      <div className="relative py-12 px-4 bg-gradient-to-br from-slate-900 via-brand-900 to-slate-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCAzLTRzMyAyIDMgNHYyYzAgMi0yIDQtMyA0cy0zLTItMy00di0yem0wLTMwYzAtMiAyLTQgMy00czMgMiAzIDR2MmMwIDItMiA0LTMgNHMtMy0yLTMtNFY0ek0wIDM0YzAtMiAyLTQgMy00czMgMiAzIDR2MmMwIDItMiA0LTMgNHMtMy0yLTMtNHYtMnptMC0zMGMwLTIgMi00IDMtNHMzIDIgMyA0djJjMCAyLTIgNC0zIDRzLTMtMi0zLTRWNHoiLz48L2c+PC9nPjwvc3ZnPg==')] animate-pulse"></div>
        </div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center justify-center p-1.5 mb-4 bg-white/10 backdrop-blur-xl rounded-full border border-white/30 px-6">
            <Sparkles className="h-4 w-4 mr-2 text-brand-300" />
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-50">{t.profile?.badge || 'Mon Espace Personnel'}</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-black text-white mb-3 font-serif italic tracking-tight">
            {t.profile?.title || 'Mon Profil'}
          </h2>
          <p className="text-lg text-slate-200 font-medium opacity-90 max-w-2xl mx-auto">
            {t.profile?.subtitle || 'Gérez vos informations personnelles'}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-16 space-y-8">
        {/* Section Informations personnelles */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-200">
          <div className="relative px-8 py-6 bg-gradient-to-br from-brand-600 to-brand-800 overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <User className="h-32 w-32 text-white absolute -right-8 -top-8" />
            </div>
            <div className="flex items-center space-x-3 relative z-10">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-xl rounded-2xl flex items-center justify-center">
                <User className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-black text-white font-serif italic">
                {t.profile?.personalInfo || 'Informations Personnelles'}
              </h3>
            </div>
          </div>

          <div className="p-8">
            {message && (
              <div className={`mb-8 p-5 rounded-2xl flex items-center font-bold ${
                message.type === 'success' 
                  ? 'bg-green-50 text-green-700 border-2 border-green-200' 
                  : 'bg-red-50 text-red-700 border-2 border-red-200'
              }`}>
                {message.type === 'success' ? (
                  <CheckCircle className="h-6 w-6 mr-3 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-6 w-6 mr-3 flex-shrink-0" />
                )}
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-black uppercase tracking-wider text-slate-700">
                    {t.auth?.firstname || 'Prénom'}
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-3.5 h-5 w-5 text-slate-400" />
                    <input 
                      type="text" 
                      name="firstname" 
                      value={formData.firstname} 
                      onChange={handleChange} 
                      className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-2xl focus:ring-4 focus:ring-brand-200 focus:border-brand-600 transition-all outline-none font-medium"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-black uppercase tracking-wider text-slate-700">
                    {t.auth?.name || 'Nom'}
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-3.5 h-5 w-5 text-slate-400" />
                    <input 
                      type="text" 
                      name="name" 
                      value={formData.name} 
                      onChange={handleChange} 
                      className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-2xl focus:ring-4 focus:ring-brand-200 focus:border-brand-600 transition-all outline-none font-medium"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-black uppercase tracking-wider text-slate-700">
                  {t.auth?.email || 'Email'}
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-3.5 h-5 w-5 text-slate-400" />
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email} 
                    disabled 
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 bg-slate-50 rounded-2xl text-slate-500 cursor-not-allowed font-medium"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1 ml-1">
                  {t.profile?.emailNotice || "L'email ne peut pas être modifié"}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-black uppercase tracking-wider text-slate-700">
                    {t.auth?.phone || 'Téléphone'}
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-3.5 h-5 w-5 text-slate-400" />
                    <input 
                      type="tel" 
                      name="phone" 
                      value={formData.phone} 
                      onChange={handleChange} 
                      className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-2xl focus:ring-4 focus:ring-brand-200 focus:border-brand-600 transition-all outline-none font-medium"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-black uppercase tracking-wider text-slate-700">
                    {t.auth?.language || 'Langue'}
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-4 top-3.5 h-5 w-5 text-slate-400" />
                    <select 
                      name="preferred_language" 
                      value={formData.preferred_language} 
                      onChange={handleChange} 
                      className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-2xl focus:ring-4 focus:ring-brand-200 focus:border-brand-600 transition-all outline-none bg-white font-medium appearance-none"
                    >
                      <option value="fr">🇫🇷 Français</option>
                      <option value="en">🇬🇧 English</option>
                      <option value="pt">🇵🇹 Português</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-black uppercase tracking-wider text-slate-700">
                  {t.auth?.address || 'Adresse'}
                </label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-3.5 h-5 w-5 text-slate-400" />
                  <input 
                    type="text" 
                    name="address" 
                    value={formData.address} 
                    onChange={handleChange} 
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-2xl focus:ring-4 focus:ring-brand-200 focus:border-brand-600 transition-all outline-none font-medium"
                  />
                </div>
              </div>

              <button 
                type="submit" 
                disabled={loading} 
                className="w-full group inline-flex items-center justify-center bg-brand-600 text-white px-10 py-4 rounded-full font-black text-lg hover:bg-brand-700 transition-all shadow-xl transform hover:-translate-y-1 hover:shadow-brand-500/40 border-b-4 border-brand-800 active:translate-y-0 active:border-b-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader className="animate-spin h-6 w-6" />
                ) : (
                  <>
                    <Save className="h-5 w-5 mr-3 group-hover:scale-110 transition-transform" />
                    {t.admin?.services?.save || 'Enregistrer'}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Section Sécurité / Mot de passe */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-200">
          <div className="relative px-8 py-6 bg-gradient-to-br from-slate-700 to-slate-900 overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <Shield className="h-32 w-32 text-white absolute -right-8 -top-8" />
            </div>
            <div className="flex items-center space-x-3 relative z-10">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-xl rounded-2xl flex items-center justify-center">
                <Lock className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-black text-white font-serif italic">
                {t.profile?.security || 'Sécurité'}
              </h3>
            </div>
          </div>
          
          <div className="p-8">
            {passMessage && (
              <div className={`mb-8 p-5 rounded-2xl flex items-center font-bold ${
                passMessage.type === 'success' 
                  ? 'bg-green-50 text-green-700 border-2 border-green-200' 
                  : 'bg-red-50 text-red-700 border-2 border-red-200'
              }`}>
                {passMessage.type === 'success' ? (
                  <CheckCircle className="h-6 w-6 mr-3 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-6 w-6 mr-3 flex-shrink-0" />
                )}
                {passMessage.text}
              </div>
            )}

            <form onSubmit={handlePasswordSubmit} className="space-y-6 max-w-xl mx-auto">
              <div className="space-y-2">
                <label className="block text-sm font-black uppercase tracking-wider text-slate-700">
                  {t.profile?.oldPassword || 'Mot de passe actuel'}
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-3.5 h-5 w-5 text-slate-400" />
                  <input 
                    type="password" 
                    name="oldPassword" 
                    required 
                    value={passData.oldPassword} 
                    onChange={handlePassChange} 
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-2xl focus:ring-4 focus:ring-slate-200 focus:border-slate-600 transition-all outline-none font-medium"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-black uppercase tracking-wider text-slate-700">
                  {t.admin?.account?.newPassword || 'Nouveau mot de passe'}
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-3.5 h-5 w-5 text-slate-400" />
                  <input 
                    type="password" 
                    name="newPassword" 
                    required 
                    value={passData.newPassword} 
                    onChange={handlePassChange} 
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-2xl focus:ring-4 focus:ring-slate-200 focus:border-slate-600 transition-all outline-none font-medium"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-black uppercase tracking-wider text-slate-700">
                  {t.admin?.account?.confirmPassword || 'Confirmer le mot de passe'}
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-3.5 h-5 w-5 text-slate-400" />
                  <input 
                    type="password" 
                    name="confirmPassword" 
                    required 
                    value={passData.confirmPassword} 
                    onChange={handlePassChange} 
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-2xl focus:ring-4 focus:ring-slate-200 focus:border-slate-600 transition-all outline-none font-medium"
                  />
                </div>
              </div>

              <button 
                type="submit" 
                disabled={passLoading} 
                className="w-full group inline-flex items-center justify-center bg-slate-800 text-white px-10 py-4 rounded-full font-black text-lg hover:bg-slate-900 transition-all shadow-xl transform hover:-translate-y-1 hover:shadow-slate-500/40 border-b-4 border-slate-900 active:translate-y-0 active:border-b-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {passLoading ? (
                  <Loader className="animate-spin h-6 w-6" />
                ) : (
                  <>
                    <Save className="h-5 w-5 mr-3 group-hover:scale-110 transition-transform" />
                    {t.admin?.account?.save || 'Modifier le mot de passe'}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}